import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, TABLES } from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowLeft } from 'react-icons/fi';
import JourneyMapAnalysis from '../../components/analysis/JourneyMapAnalysis';

const SnapshotView = () => {
  const { projectId, snapshotId } = useParams();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personas, setPersonas] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const saveTimer = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from(TABLES.JOURNEY_SNAPSHOTS)
        .select('*')
        .eq('id', snapshotId)
        .maybeSingle();
      if (active) {
        setSnapshot(data);
        setPersonas(data?.personas || []);
        setQuotes(data?.quotes || []);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [snapshotId]);

  // Debounced persistence of annotation edits to the snapshot row
  const scheduleSave = (patch) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const { error } = await supabase.from(TABLES.JOURNEY_SNAPSHOTS).update(patch).eq('id', snapshotId);
      if (error) console.error('[snapshot] save annotations failed:', error);
    }, 600);
  };
  const handlePersonas = (next) => { setPersonas(next); scheduleSave({ personas: next }); };
  const handleQuotes = (next) => { setQuotes(next); scheduleSave({ quotes: next }); };
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rutabaga-pink" />
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-rutabaga-dark">Snapshot not found</p>
          <button
            onClick={() => navigate(`/projects/${projectId}/snapshots`)}
            className="text-rutabaga-pink text-sm font-bold mt-2"
          >
            Back to snapshots
          </button>
        </div>
      </div>
    );
  }

  const sessions = snapshot.data?.sessions || [];
  const lanes = snapshot.data?.lanes || [];
  const lockedChips = (snapshot.filter_config || [])
    .filter(d => (d.selected || []).length > 0)
    .map(d => ({
      label: d.label,
      values: (d.selected || []).map(v => {
        const opt = (d.values || []).find(o => o.value === v);
        return opt?.label || v;
      }).join(', '),
    }));

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <button
          onClick={() => navigate(`/projects/${projectId}/snapshots`)}
          className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-700 transition-colors mb-2"
        >
          <SafeIcon icon={FiArrowLeft} /> Snapshots
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-black text-rutabaga-dark tracking-tight">{snapshot.name}</h1>
            {snapshot.description && (
              <p className="text-sm text-gray-500 mt-1 max-w-2xl">{snapshot.description}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {lockedChips.map((c, i) => (
              <span key={i} className="text-[9px] font-black uppercase tracking-wide bg-rutabaga-pink/10 text-rutabaga-pink px-2 py-1 rounded-full"
                title="Locked segment for this snapshot">
                {c.label}: {c.values}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Read-only journey */}
      <div className="flex-1 overflow-hidden">
        <JourneyMapAnalysis
          sessions={sessions}
          lanes={lanes}
          module={{ id: null, config: { h_stretch: snapshot.h_stretch, tag_positions: snapshot.tag_positions } }}
          initialViewMode={snapshot.view_mode}
          readOnly
          annotationMode
          editable
          personas={personas}
          onPersonasChange={handlePersonas}
          quotes={quotes}
          onQuotesChange={handleQuotes}
        />
      </div>
    </div>
  );
};

export default SnapshotView;
